/**
 * Shared API error handler for 401 Unauthorized responses
 * Signs out user and redirects to login when backend token is expired
 */

import { isMsalInitializing } from "@/lib/msal/instance";
import { isTestAuth } from "@/lib/testAuth";

export async function handle401Error(): Promise<void> {
  // Skip logout entirely in test/E2E mode - 401s are expected with test tokens
  if (isTestAuth()) {
    return;
  }

  // Don't trigger logout while MSAL is still initializing (processing redirect).
  // During login flow, API calls may fire before the backend token exchange
  // completes -- those 401s are expected and should not force a logout.
  if (isMsalInitializing) {
    return;
  }

  const { localLogout } = await import("@/lib/localLogout");
  await localLogout("/login?expired=true");
}

/**
 * Handle 403 Forbidden errors (typically consent required)
 */
export async function handle403Error(message?: string): Promise<void> {
  // Skip logout entirely in test/E2E mode
  if (isTestAuth()) {
    return;
  }

  // Don't trigger logout while MSAL is still initializing
  if (isMsalInitializing) {
    return;
  }

  // "No organization context" means TenantId dependency rejected -- user is
  // authenticated but their JWT has no tenant_id. Don't force a logout; the
  // UI should show an error banner instead.
  if (message?.includes("organization context")) {
    console.warn("403: Missing tenant context -- user needs org assignment");
    return;
  }

  const { localLogout } = await import("@/lib/localLogout");
  await localLogout("/login?consent_required=true");
}

/**
 * Check if response is 401 and handle it
 */
export function checkAndHandle401(response: Response): boolean {
  if (response.status === 401) {
    handle401Error().catch(console.error);
    return true;
  }
  return false;
}
